import { useState } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { PasscodeGate } from './components/PasscodeGate.js';
import { Onboarding } from './components/Onboarding.js';
import { MatchResults } from './components/MatchResults.js';
import { Chat, type Turn } from './components/Chat.js';
import { Wrap } from './components/Wrap.js';
import { Alumni } from './components/Alumni.js';
import { Operator } from './components/Operator.js';
import { UserList } from './components/UserList.js';
import { UserPlanning } from './components/UserPlanning.js';
import { FlywheelIndicator } from './components/FlywheelIndicator.js';
import { FlywheelIntro } from './components/FlywheelIntro.js';
import { FlywheelProvider } from './lib/flywheel.js';
import { getUser, type User } from './lib/users.js';
import type { MatchInput, MatchResult } from './lib/api.js';

/**
 * Top-level routing. The app starts at "/" with a user-list (demo "login")
 * and dispatches by `user.status` into the role-specific landing:
 *
 *   /                  → UserList (3 personas)
 *   /user/miki         → CandidateFlow      (status: pre-register)
 *   /user/sora         → UserPlanning       (status: pre-visit)
 *   /user/takumi       → Alumni             (status: post-visit)
 *   /operator          → Operator           (separate category, not in user list)
 *
 * Wrapped in FlywheelProvider so any screen can emit events and the topbar
 * indicator + flywheel intro/closing pages can subscribe.
 *
 * No auth (passcode is just a demo gate). No localStorage. Reload =
 * start over.
 */

type CandidateScreen =
  | { kind: 'onboarding' }
  | { kind: 'match'; input: MatchInput }
  | { kind: 'chat'; agent: MatchResult; input: MatchInput }
  | { kind: 'wrap'; agent: MatchResult; turns: Turn[]; input: MatchInput };

type Overlay = null | 'flywheel-intro' | 'flywheel-closing';

export default function App() {
  return (
    <BrowserRouter>
      <FlywheelProvider>
        <AppShell />
      </FlywheelProvider>
    </BrowserRouter>
  );
}

function AppShell() {
  const [passed, setPassed] = useState(false);
  // Flywheel intro shows once on first entry; closing is reachable via the
  // topbar pill at any time.
  const [overlay, setOverlay] = useState<Overlay>('flywheel-intro');

  return (
    <div className="shell">
      <header className="topbar">
        <Link to="/" className="topbar-brand">
          D-COMPASS × SENPAI
        </Link>
        {passed ? (
          <div className="topbar-actions">
            <FlywheelIndicator onClick={() => setOverlay('flywheel-closing')} />
            <Link to="/" className="topbar-link">
              ユーザー切替
            </Link>
            <Link to="/operator" className="topbar-link topbar-link-operator">
              運営
            </Link>
          </div>
        ) : (
          <span className="topbar-trail" />
        )}
      </header>

      {!passed && <PasscodeGate onPass={() => setPassed(true)} />}

      {passed && overlay === 'flywheel-intro' && (
        <FlywheelIntro variant="intro" onContinue={() => setOverlay(null)} />
      )}

      {passed && overlay === 'flywheel-closing' && (
        <FlywheelIntro variant="closing" onContinue={() => setOverlay(null)} />
      )}

      {passed && !overlay && (
        <Routes>
          <Route path="/" element={<UserList />} />
          <Route path="/user/:id" element={<UserRoute />} />
          <Route path="/operator" element={<Operator />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </div>
  );
}

/**
 * Dispatches by the selected user's status into the right landing surface.
 * Unknown id → bounce to user-list.
 */
function UserRoute() {
  const { id } = useParams<{ id: string }>();
  const user = id ? getUser(id) : undefined;
  if (!user) return <Navigate to="/" replace />;

  switch (user.status) {
    case 'pre-register':
      return <CandidateFlow user={user} />;
    case 'pre-visit':
      return <UserPlanning user={user} />;
    case 'post-visit':
      // For the demo, post-visit users map 1:1 onto a senpai_id. Today
      // that's takumi → 'takumi'. If we add more post-visit personas
      // post-demo, this mapping becomes a `user.senpai_id` field.
      return <Alumni senpaiId={user.id} displayName={user.display_name} />;
  }
}

function CandidateFlow({ user: _user }: { user: User }) {
  const [screen, setScreen] = useState<CandidateScreen>({ kind: 'onboarding' });
  const navigate = useNavigate();
  return (
    <>
      {screen.kind === 'onboarding' && (
        <Onboarding onSubmit={(input) => setScreen({ kind: 'match', input })} />
      )}
      {screen.kind === 'match' && (
        <MatchResults
          input={screen.input}
          onPick={(agent) => setScreen({ kind: 'chat', agent, input: screen.input })}
          onBack={() => setScreen({ kind: 'onboarding' })}
        />
      )}
      {screen.kind === 'chat' && (
        <Chat
          agent={screen.agent}
          onWrap={(turns) =>
            setScreen({ kind: 'wrap', agent: screen.agent, turns, input: screen.input })
          }
          onChangeAgent={() => setScreen({ kind: 'match', input: screen.input })}
        />
      )}
      {screen.kind === 'wrap' && (
        <Wrap
          agent={screen.agent}
          turns={screen.turns}
          input={screen.input}
          onRestart={() => navigate('/')}
        />
      )}
    </>
  );
}
